import { LinkedItem, Morphs, Textures, Zone } from '@types'
import type { Textures as TexturesType } from '@types'
import { isZocaloZone } from '@utils/debug/compareLinkedItemMaterials'
import { Box3, Mesh, Object3D } from 'three'
import { Vector3 } from 'three'

/**
 * Encimera, costado, zócalo y frontal sin morphs en API (modificarAncho/Fondo/Alto en cabecera).
 * Sidebar y escala 3D como unidad universal (grupo scale).
 */
export const FREE_DIMENSION_TPC_NO_MORPH_CATALOG_IDS = [
  'ENCIMERA B3200',
  'TPC COSTADO ENCIMERA',
  'ZOCALO 4000',
  'FRONTAL',
] as const

/**
 * Encimeras TPC: ancho/fondo libres (escala grupo); morph Alto del API con min/max (p. ej. 10–30 mm).
 */
export const FREE_DIMENSION_ENCIMERA_ANCHO_CATALOG_IDS = [
  'ENCIMERA ANCHO 100',
  'ENCIMERA ANCHO 120',
  'ENCIMERA ANCHO 150',
  'ENCIMERA ANCHO 20',
  'ENCIMERA ANCHO 200',
  'ENCIMERA ANCHO 30',
  'ENCIMERA ANCHO 40',
  'ENCIMERA ANCHO 50',
  'ENCIMERA ANCHO 60',
  'ENCIMERA ANCHO 80',
  'ENCIMERA ANCHO 90',
] as const

/**
 * Laterales cascada: ancho fijo por catálogo; alto por morph Alto API; fondo libre por escala grupo.
 */
export const FREE_DIMENSION_LATERAL_CASCADA_ANCHO_CATALOG_IDS = [
  'LATERAL CASCADA 20',
  'LATERAL CASCADA 30',
  'LATERAL CASCADA 40',
  'LATERAL CASCADA 50',
  'LATERAL CASCADA 60',
  'LATERAL CASCADA 80',
  'LATERAL CASCADA 90',
  'LATERAL CASCADA 100',
  'LATERAL CASCADA 120',
  'LATERAL CASCADA 150',
  'LATERAL CASCADA 200',
] as const

/** TPC sin morphs + encimeras ancho (todas en FREE_DIMENSION_LINKED_CATALOG_IDS). */
export const FREE_DIMENSION_NO_MORPH_TPC_CATALOG_IDS = [
  ...FREE_DIMENSION_TPC_NO_MORPH_CATALOG_IDS,
  ...FREE_DIMENSION_ENCIMERA_ANCHO_CATALOG_IDS,
  ...FREE_DIMENSION_LATERAL_CASCADA_ANCHO_CATALOG_IDS,
] as const

/**
 * Unidades sin morphs en getUnidad (morphs: null).
 * Ancho/Fondo/Alto se simulan escalando el grupo 3D (todas las mallas igual).
 */
export const FREE_DIMENSION_LINKED_CATALOG_IDS = [
  ...FREE_DIMENSION_NO_MORPH_TPC_CATALOG_IDS,
  'CB9060',
  'CC14060',
  'CC15060',
  'CC16060',
  'CC23060',
  'CC24060',
  'LA3035',
  'LA3235',
  'LA3535',
  'LA3735',
  'LA4035',
  'LA4235',
  'LA4535',
  'LA4735',
  'LA5035',
  'LA5235',
  'LA8035',
  'LA8235',
  'LA9035',
  'LA9235',
  'LB8060',
  'LC13860',
  'LC14060',
  'LC14860',
  'LC15060',
  'LC22060',
  'LC23060',
  'RA805',
  'RA825',
  'RA905',
  'RA925',
  'RB805',
  'RB905',
  'RC1405',
  'RC1505',
  'RC2205',
  'RC2305',
] as const

/** Offset alto regletas RB/RC (store = catálogo + 100). Laterales LA/LB/LC/CC/CB/RA: 0. */
export const FREE_DIMENSION_ALTO_OFFSET_MM = 100

/** LB8060 / CB9060: lateral MBJ + encimera (sync ancho/fondo hijo; alto store = catálogo 900). */
export const FREE_DIMENSION_LB8060_STYLE_CATALOG_IDS = ['LB8060', 'CB9060'] as const

/** Columnas CC: store +100 y escala layout×(cat+100)/(scaleBase×cat). */
export const FREE_DIMENSION_CC_COLUMN_CATALOG_IDS = [
  'CC14060',
  'CC15060',
  'CC16060',
  'CC23060',
  'CC24060',
] as const

/** Columnas RA: store +100 y escala layout×(cat+100)/(scaleBase×cat). */
export const FREE_DIMENSION_RA_COLUMN_CATALOG_IDS = [
  'RA805',
  'RA825',
  'RA905',
  'RA925',
] as const

/** LC23060: store = catálogo − 100 (sidebar sigue en catálogo). */
export const FREE_DIMENSION_ALTO_OFFSET_MINUS_100_CATALOG_IDS = ['LC23060'] as const

const DIMENSION_MORPH_NAMES = ['Ancho', 'Fondo', 'Alto'] as const

export type FreeDimensionMm = { x: number; y: number; z: number }
export type FreeDimensionGlbBaseMm = FreeDimensionMm
export type FreeDimensionCatalogMm = FreeDimensionMm
export type FreeDimensionScaleBaseMm = FreeDimensionMm

export function getLinkedItemCatalogCode(
  item: { catalogID?: string | number; codigo?: string | number } | null | undefined,
): string {
  if (!item) return ''
  const id = item.catalogID ?? item.codigo
  return id != null ? String(id).trim().toUpperCase() : ''
}

export function isFreeDimensionLinkedItem(
  item: { catalogID?: string | number; codigo?: string | number } | null | undefined,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_LINKED_CATALOG_IDS as readonly string[]).includes(code)
}

/** Catálogos regleta/columna sin morphs de malla: escala por grupo como universal (ancho/alto/fondo en store). */
export function usesFreeDimensionScaling(
  item: { catalogID?: string | number; codigo?: string | number } | null | undefined,
): boolean {
  return isFreeDimensionLinkedItem(item)
}

export function usesFreeDimensionAltoOffset(
  item: { catalogID?: string | number; codigo?: string | number } | null | undefined,
): boolean {
  return usesFreeDimensionScaling(item) && getFreeDimensionAltoOffsetMm(item) !== 0
}

/** Encimera ancho TPC: morph Alto API (grosor 10–30 mm), ancho/fondo por escala grupo. */
export function isFreeDimensionEncimeraAnchoCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_ENCIMERA_ANCHO_CATALOG_IDS as readonly string[]).includes(code)
}

/** Lateral cascada ancho: morph Alto API, fondo por escala grupo. */
export function isFreeDimensionLateralCascadaAnchoCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_LATERAL_CASCADA_ANCHO_CATALOG_IDS as readonly string[]).includes(code)
}

/** Catálogos free-dim que mantienen rango de morph Alto del API. */
export function isFreeDimensionAltoApiMorphCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  return (
    isFreeDimensionEncimeraAnchoCatalog(item) || isFreeDimensionLateralCascadaAnchoCatalog(item)
  )
}

/** Catálogos free-dim que deben respetar rango API del morph Ancho. */
export function isFreeDimensionAnchoApiMorphCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  return isFreeDimensionLateralCascadaAnchoCatalog(item)
}

/** Encimera / costado / zócalo / frontal sin morphs: todo por escala grupo. */
export function isFreeDimensionTpcNoMorphCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_TPC_NO_MORPH_CATALOG_IDS as readonly string[]).includes(code)
}

export function morphHasValidRange(morph?: Morphs | null): boolean {
  if (!morph) return false
  const min = morph.min ?? 0
  const max = morph.max ?? 0
  return max > min
}

export function clampMorphMm(value: number, min?: number | null, max?: number | null): number {
  let v = value
  if (min != null && v < min) v = min
  if (max != null && v > max) v = max
  return v
}

/** Alto con rango API en encimeras ancho: deformar mesh, no escala Y del grupo. */
export function itemUsesFreeDimensionAltoApiMorph(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    morphs?: Morphs[]
  } | null,
): boolean {
  if (!isFreeDimensionAltoApiMorphCatalog(item)) return false
  const alto = item?.morphs?.find((m) => m.name === 'Alto')
  return morphHasValidRange(alto)
}

/** Morphs de malla (p. ej. Alto 10–30) aunque ancho/fondo usen escala free-dim. */
export function linkedItemUsesMeshMorphs(
  item?: {
    hasMorphs?: boolean
    catalogID?: string | number
    codigo?: string | number
    morphs?: Morphs[]
  } | null,
): boolean {
  if (!item?.hasMorphs) return false
  if (!usesFreeDimensionScaling(item)) return true
  return itemUsesFreeDimensionAltoApiMorph(item)
}

/** Laterales y costados (LA/LB/LC/CC/CB/RA): alto store = catálogo, sin +100. */
export function isFreeDimensionLateralOrCostadoCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  if (!code) return false
  return /^(LA|LB|LC|CC|CB|RA)/.test(code)
}

/**
 * Bbox GLB más alto que catálogo y sin offset en store (p. ej. LC14060: 1500 vs 1400).
 * El exceso suele ser la losa/zócalo del mesh; la escala Y del grupo es layout/glbY.
 */
export function usesFreeDimensionGlbZocaloSlabInAltoBbox(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    morphs?: Morphs[]
  } | null,
): boolean {
  if (!item || !isFreeDimensionLateralOrCostadoCatalog(item)) return false
  if (getFreeDimensionAltoOffsetMm(item) !== 0) return false
  if (itemUsesFreeDimensionAltoGlbMorph(item)) return false
  const catY = item.freeDimensionCatalogMm?.y ?? 0
  const glbY = item.freeDimensionGlbBaseMm?.y ?? 0
  return catY > 0 && glbY > catY + 1
}

/** Escala Y del grupo: item.height (mm store) / bbox GLB, como unidades universales sin morphs. */
export function getFreeDimensionAltoGroupScaleY(
  item: {
    height?: number
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    glbSize?: Vector3
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  } | null | undefined,
): number {
  if (itemUsesFreeDimensionAltoApiMorph(item)) return 1
  const target = getFreeDimensionAltoScaleTargetMm(item)
  if (target <= 0) return 1
  const glbY =
    item?.freeDimensionGlbBaseMm?.y ??
    (item?.glbSize?.y != null && item.glbSize.y > 0 ? Math.round(item.glbSize.y * 1000) : 0)
  if (glbY <= 0) return 1
  return target / glbY
}

/** mm en store (−100 LC23060, +100 RB/RC regletas; TPC encimera/zócalo/frontal y laterales: 0). */
export function getFreeDimensionAltoOffsetMm(
  item?: { catalogID?: string | number; codigo?: string | number; hasMorphs?: boolean } | null,
): number {
  if (!item || !usesFreeDimensionScaling(item)) return 0
  const code = getLinkedItemCatalogCode(item)
  if ((FREE_DIMENSION_ALTO_OFFSET_MINUS_100_CATALOG_IDS as readonly string[]).includes(code)) {
    return -FREE_DIMENSION_ALTO_OFFSET_MM
  }
  if (
    isFreeDimensionTpcNoMorphCatalog(item) ||
    isFreeDimensionAltoApiMorphCatalog(item) ||
    isFreeDimensionLateralOrCostadoCatalog(item)
  ) {
    return 0
  }
  return FREE_DIMENSION_ALTO_OFFSET_MM
}

export function getFreeDimensionStoreAltoAtPlace(
  catalogBodyMm: number,
  item: { catalogID?: string | number; codigo?: string | number },
): number {
  return catalogBodyMm + getFreeDimensionAltoOffsetMm(item)
}

/** Alto store al colocar: RB usa posicionY encimera del API (p. ej. alto 800 → encimera 900). */
export function getFreeDimensionPlacementStoreAltoMm(
  backGLBInfo?: FreeDimensionPlacementSource | null,
  catalogBodyAltoMm?: number,
  catalogID?: string | number,
): number {
  const bodyMm = catalogBodyAltoMm ?? backGLBInfo?.alto ?? 0
  if (isRbFreeDimensionRegleta({ catalogID })) {
    const enc = backGLBInfo?.elementosEnlazados?.find((e) =>
      String(e.descripcion ?? '').toLowerCase().includes('encimera'),
    )
    const encY = enc?.posicionY
    if (bodyMm > 0 && typeof encY === 'number' && encY >= bodyMm) return encY
  }
  return getFreeDimensionStoreAltoAtPlace(bodyMm, { catalogID })
}

/** Alto activo en sidebar para encimera/zócalo/frontal TPC (sin offset +100 legacy). */
function getFreeDimensionTpcNoMorphLayoutAltoMm(
  item?: {
    height?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
  } | null,
): number {
  if (!item) return 0
  const morphAlto = item.morphs?.find((m) => m.name === 'Alto')
  const fromMorph = morphAlto?._default
  if (fromMorph != null && fromMorph > 0) return fromMorph
  const storeH = item.height ?? 0
  const catY = item.freeDimensionCatalogMm?.y ?? 0
  // Corregir height guardado con +100 mm de regletas (p. ej. store 110 / morph 10).
  if (catY > 0 && storeH === catY + FREE_DIMENSION_ALTO_OFFSET_MM) {
    return fromMorph != null && fromMorph > 0 ? fromMorph : catY
  }
  if (storeH > 0) return storeH
  return catY
}

export function getFreeDimensionSidebarAlto(
  storeHeight?: number,
  item?: {
    catalogID?: string | number
    codigo?: string | number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
    height?: number
  } | null,
): number {
  if (isFreeDimensionTpcNoMorphCatalog(item)) {
    return getFreeDimensionTpcNoMorphLayoutAltoMm({
      ...item,
      height: storeHeight ?? item?.height,
    })
  }
  const offset = getFreeDimensionAltoOffsetMm(item)
  if (storeHeight == null || storeHeight <= 0) {
    return item?.freeDimensionCatalogMm?.y ?? 0
  }
  return Math.max(0, storeHeight - offset)
}

export function getFreeDimensionStoreAlto(
  sidebarAltoMm: number,
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): number {
  return sidebarAltoMm + getFreeDimensionAltoOffsetMm(item)
}

/** Valor morph GLB / item.height (mm) = sidebar + zócalo (+100 RB/RC, −100 LC…). */
export function getFreeDimensionAltoGlbMorphMm(
  sidebarAltoMm: number,
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): number {
  return getFreeDimensionStoreAlto(sidebarAltoMm, item)
}

/** Máximo sidebar (cuerpo sin zócalo) a partir del max del morph GLB. */
export function getFreeDimensionAltoSidebarMorphMaxMm(
  morphMaxMm: number,
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): number {
  const offset = getFreeDimensionAltoOffsetMm(item)
  if (offset === 0 || morphMaxMm <= 0) return morphMaxMm
  return Math.max(0, morphMaxMm - offset)
}

/** LB8060, CB9060: lateral MBJ + encimera (sync ancho/fondo hijo; alto = catálogo). */
export function freeDimensionUsesLb8060StyleAltoOffset(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_LB8060_STYLE_CATALOG_IDS as readonly string[]).includes(code)
}

/**
 * Bbox X mucho mayor que catálogo/layout → el cuerpo usa morph Ancho del GLB, no scale X del grupo.
 * (RB805: bbox 190 mm, regleta real ~50 mm; escalar el grupo deja una tira invisible).
 */
export function resolveFreeDimensionAnchoViaGlbMorph(
  glbBaseMm: FreeDimensionGlbBaseMm,
  catalogMm: FreeDimensionCatalogMm,
  layoutWidthMm: number,
): boolean {
  const box = glbBaseMm.x
  const cat = catalogMm.x
  if (box > 0 && cat > 0 && box > cat * 1.15) return true
  if (box > 0 && layoutWidthMm > 0 && box > layoutWidthMm * 2.5) return true
  return false
}

export function getFreeDimensionAnchoMorphMaxMm(
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
): number {
  if (morph?.max != null && morph.min != null && morph.max > morph.min) return morph.max
  const cat = catalogMm?.x ?? 0
  return cat > 0 ? cat : 100
}

/** Encimera por defecto en regletas RB (API suele traer B10; producto usa B80new). */
export const RB_REGLETA_ENCIMERA_CODIGO = 'Encimera B80new'

/** Bbox Z &gt; catálogo: fondo por morph GLB, no solo scale Z del grupo. */
export function resolveFreeDimensionFondoViaGlbMorph(
  glbBaseMm: FreeDimensionGlbBaseMm,
  catalogMm: FreeDimensionCatalogMm,
  catalogID?: string | number,
): boolean {
  // RB805: sin morph Fondo útil en malla (min≈max) → escala grupo en Z, no morph.
  if (isRbFreeDimensionRegleta({ catalogID })) return false
  const box = glbBaseMm.z
  const cat = catalogMm.z
  if (box > 0 && cat > 0 && box > cat * 1.08) return true
  return false
}

export function getFreeDimensionFondoMorphMaxMm(
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
  glbBaseMm?: FreeDimensionGlbBaseMm,
): number {
  if (morph?.max != null && morph.min != null && morph.max > morph.min) return morph.max
  const cat = catalogMm?.z ?? 0
  const glb = glbBaseMm?.z ?? 0
  if (glb > cat && cat > 0) return glb
  return cat > 0 ? cat : 600
}

/** Escala Z extra cuando fondo sidebar &gt; morph.max del GLB. */
export function getFreeDimensionFondoGlbMorphScaleZ(
  thicknessMm: number,
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
): number {
  const t = thicknessMm > 0 ? thicknessMm : 0
  if (t <= 0) return 1
  const max = getFreeDimensionFondoMorphMaxMm(morph, catalogMm, undefined)
  return t > max ? t / max : 1
}

export function applyFreeDimensionFondoGlbMorphMorph(
  morph: Morphs,
  layoutThicknessMm: number,
  morphMaxMm: number,
): Morphs {
  const min =
    morph.min != null && morph.max != null && morph.max > morph.min ? morph.min : 10
  const max = morphMaxMm > min ? morphMaxMm : min + 1
  const t = layoutThicknessMm > 0 ? layoutThicknessMm : max
  const morphDefault = Math.min(Math.max(t, min), max)
  return {
    ...morph,
    min,
    max,
    default: morphDefault,
    _default: t,
    show: true,
    modifyable: true,
  }
}

/** Escala X extra cuando ancho sidebar &gt; morph.max del GLB (p. ej. RB805: 350 mm con morph máx. 100). */
export function getFreeDimensionAnchoGlbMorphScaleX(
  widthMm: number,
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
): number {
  const w = widthMm > 0 ? widthMm : 0
  if (w <= 0) return 1
  const max = getFreeDimensionAnchoMorphMaxMm(morph, catalogMm)
  return w > max ? w / max : 1
}

/**
 * Alto siempre por escala de grupo (ratios.y), no morph GLB.
 * El morph en Y deforma puerta/zócalo/encimera y desincroniza catalogY×ratio.
 * Ancho sí usa morph cuando bbox ≫ catálogo (RB805, regletas).
 */
export function resolveFreeDimensionAltoViaGlbMorph(
  _glbBaseMm: FreeDimensionGlbBaseMm,
  _catalogMm: FreeDimensionCatalogMm,
  _altoOffsetMm: number,
): boolean {
  return false
}

export function getFreeDimensionAltoMorphMaxMm(
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
  altoOffsetMm = 0,
): number {
  if (morph?.max != null && morph.min != null && morph.max > morph.min) return morph.max
  const cat = catalogMm?.y ?? 0
  return cat > 0 ? cat + altoOffsetMm : 900
}

export function getFreeDimensionAltoGlbMorphScaleY(
  sidebarAltoMm: number,
  morph: Morphs | undefined,
  item?: {
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
  },
): number {
  const layout = sidebarAltoMm > 0 ? sidebarAltoMm : 0
  if (layout <= 0) return 1
  const offset = getFreeDimensionAltoOffsetMm(item)
  const max = getFreeDimensionAltoMorphMaxMm(morph, item?.freeDimensionCatalogMm, offset)
  return layout > max ? layout / max : 1
}

export function applyFreeDimensionAltoGlbMorphMorph(
  morph: Morphs,
  layoutAltoMm: number,
  catalogAltoMm: number,
  altoOffsetMm: number,
): Morphs {
  const cat = catalogAltoMm > 0 ? catalogAltoMm : 800
  const min = cat
  const max = cat + (altoOffsetMm > 0 ? altoOffsetMm : FREE_DIMENSION_ALTO_OFFSET_MM)
  const h = layoutAltoMm > 0 ? layoutAltoMm : cat
  const morphDefault = Math.min(
    altoOffsetMm !== 0 ? h + altoOffsetMm : h,
    max,
  )
  return {
    ...morph,
    min,
    max,
    default: morphDefault,
    _default: h,
    show: true,
    modifyable: true,
  }
}

export function applyFreeDimensionAnchoGlbMorphMorph(
  morph: Morphs,
  layoutWidthMm: number,
  catalogWidthMm: number,
): Morphs {
  const max = catalogWidthMm > 0 ? catalogWidthMm : 100
  const min = 10
  const w = layoutWidthMm > 0 ? layoutWidthMm : 50
  const morphDefault = Math.min(w, max)
  return {
    ...morph,
    min,
    max,
    default: morphDefault,
    _default: w,
    show: true,
    modifyable: true,
  }
}

/**
 * Morphs al colocar.
 * - Ancho (si bbox ≫ catálogo): morph GLB hasta max catálogo + scaleX si sidebar &gt; max.
 * - Alto (bbox con losa): morph GLB cat…cat+offset + scaleY extra si sidebar &gt; max.
 * - Resto: ref GLB en default, activo en _default → scale grupo.
 */
export function applyFreeDimensionUniversalStyleMorphs(
  morphs: Morphs[],
  layout: { width: number; height: number; thickness: number },
  glbBaseMm: FreeDimensionGlbBaseMm,
  catalogMm: FreeDimensionCatalogMm,
  anchoViaGlbMorph = false,
  altoViaGlbMorph = false,
  fondoViaGlbMorph = false,
  altoOffsetMm = 0,
  preserveAltoApiMorphRange = false,
  preserveAnchoApiMorphRange = false,
): Morphs[] {
  const scaleBase = resolveFreeDimensionScaleBaseMm(catalogMm, glbBaseMm)

  const layoutByName = {
    Ancho: layout.width,
    Alto: layout.height,
    Fondo: layout.thickness,
  } as const

  const glbRefByName = {
    Ancho: glbBaseMm.x > 0 ? glbBaseMm.x : scaleBase.x,
    Alto: glbBaseMm.y > 0 ? glbBaseMm.y : scaleBase.y,
    Fondo: glbBaseMm.z > 0 ? glbBaseMm.z : scaleBase.z,
  } as const

  return morphs.map((m) => {
    if (!isFreeDimensionDimensionMorph(m.name)) return m

    if (anchoViaGlbMorph && m.name === 'Ancho') {
      return applyFreeDimensionAnchoGlbMorphMorph(m, layout.width, catalogMm.x)
    }

    if (altoViaGlbMorph && m.name === 'Alto') {
      return applyFreeDimensionAltoGlbMorphMorph(m, layout.height, catalogMm.y, altoOffsetMm)
    }

    if (fondoViaGlbMorph && m.name === 'Fondo') {
      const morphMax = getFreeDimensionFondoMorphMaxMm(m, catalogMm, glbBaseMm)
      return applyFreeDimensionFondoGlbMorphMorph(m, layout.thickness, morphMax)
    }

    if (preserveAltoApiMorphRange && m.name === 'Alto' && morphHasValidRange(m)) {
      const active = clampMorphMm(layout.height, m.min, m.max)
      return {
        ...m,
        _default: active,
        default: active,
        min: m.min,
        max: m.max,
        show: true,
        modifyable: true,
      }
    }

    if (preserveAnchoApiMorphRange && m.name === 'Ancho' && morphHasValidRange(m)) {
      const active = clampMorphMm(layout.width, m.min, m.max)
      return {
        ...m,
        _default: active,
        default: active,
        min: m.min,
        max: m.max,
        show: true,
        modifyable: true,
      }
    }

    const active = layoutByName[m.name as keyof typeof layoutByName] ?? m.default ?? 0
    const ref = glbRefByName[m.name as keyof typeof glbRefByName] ?? active
    const glbAltoIncludesZocaloSlab =
      m.name === 'Alto' &&
      altoOffsetMm === 0 &&
      catalogMm.y > 0 &&
      glbBaseMm.y > catalogMm.y + 1
    return {
      ...m,
      _default: active,
      // Laterales/columnas: bbox GLB incluye ~100 mm de zócalo; escala grupo = layout/glbY.
      default: glbAltoIncludesZocaloSlab ? active : ref,
      min: 0,
      max: 0,
      show: true,
      modifyable: true,
    }
  })
}

/**
 * Escala Z del grupo padre free-dim (mismo criterio que universal sin hasMorphs):
 * thickness / refGlbZ. No usar scaleBase.z si difiere del ref (fórmula panel 2×cat−bbox).
 */
export function getFreeDimensionFondoGroupScaleZ(
  item: {
    thickness?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  } | null | undefined,
  fondoMorph?: Morphs,
): number {
  if (!item) return 1
  const m = fondoMorph ?? item.morphs?.find((x) => x.name === 'Fondo')
  const active = item.thickness ?? m?._default ?? 0
  // Priorizar ref de catálogo/GLB (morph.default pudo guardarse mal como valor activo en datos viejos).
  const ref =
    item.freeDimensionGlbBaseMm?.z ||
    item.freeDimensionCatalogMm?.z ||
    item.freeDimensionScaleBaseMm?.z ||
    (m?.default != null && m.default > 0 ? m.default : 0) ||
    0
  if (ref <= 0 || active <= 0) return 1
  return active / ref
}

export function itemUsesFreeDimensionAnchoGlbMorph(
  item?: {
    freeDimensionAnchoViaGlbMorph?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    width?: number
    catalogID?: string | number
    hasMorphs?: boolean
  } | null,
): boolean {
  if (!item || !usesFreeDimensionScaling(item)) return false
  if (isFreeDimensionAnchoApiMorphCatalog(item)) return false
  if (item.freeDimensionAnchoViaGlbMorph === true) return true
  if (item.freeDimensionAnchoViaGlbMorph === false) return false
  if (item.freeDimensionGlbBaseMm && item.freeDimensionCatalogMm) {
    return resolveFreeDimensionAnchoViaGlbMorph(
      item.freeDimensionGlbBaseMm,
      item.freeDimensionCatalogMm,
      item.width ?? 0,
    )
  }
  return isRbFreeDimensionRegleta(item)
}

export function itemUsesFreeDimensionFondoGlbMorph(
  item?: {
    freeDimensionFondoViaGlbMorph?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    hasMorphs?: boolean
  } | null,
): boolean {
  if (!item || !usesFreeDimensionScaling(item)) return false
  if (isRbFreeDimensionRegleta(item)) return false
  if (item.freeDimensionFondoViaGlbMorph === true) return true
  if (item.freeDimensionFondoViaGlbMorph === false) return false
  if (item.freeDimensionGlbBaseMm && item.freeDimensionCatalogMm) {
    return resolveFreeDimensionFondoViaGlbMorph(
      item.freeDimensionGlbBaseMm,
      item.freeDimensionCatalogMm,
      item.catalogID ?? item.codigo,
    )
  }
  return false
}

export function itemUsesFreeDimensionAltoGlbMorph(
  _item?: {
    freeDimensionAltoViaGlbMorph?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  } | null,
): boolean {
  return false
}

/** Tope Y (mm) del cuerpo escalado = base para posicionar encimera (hijo fuera del grupo). */
export function getFreeDimensionVisualBodyTopYMm(
  item: {
    height?: number
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
  } | null | undefined,
): number {
  if (!usesFreeDimensionScaling(item)) return item?.height ?? 0

  const sidebarAlto =
    getFreeDimensionParentLayoutAltoMm(item) ??
    getFreeDimensionSidebarAlto(item?.height, item) ??
    item?.freeDimensionCatalogMm?.y ??
    0

  const glbY = item?.freeDimensionGlbBaseMm?.y ?? 0
  const altoMorph = item?.morphs?.find((m) => m.name === 'Alto')

  if (itemUsesFreeDimensionAltoGlbMorph(item)) {
    if (glbY > 0) {
      const ratios = getFreeDimensionScaleRatios(item)
      return Math.round(glbY * ratios.y)
    }
    const bodyAlto = altoMorph?._default ?? sidebarAlto
    const extraY = getFreeDimensionAltoGlbMorphScaleY(bodyAlto, altoMorph, item)
    return Math.round(bodyAlto * extraY)
  }

  const target =
    getFreeDimensionAltoScaleTargetMm(item) || sidebarAlto
  if (glbY > 0 && target > 0) {
    return Math.round(glbY * getFreeDimensionAltoGroupScaleY(item))
  }
  return target
}

/** Tope Y (mm) del mesh padre escalado; coords locales (hijo fuera del grupo scale). */
export function getFreeDimensionScaledMeshTopYMm(
  item: Parameters<typeof getFreeDimensionVisualBodyTopYMm>[0],
): number {
  return getFreeDimensionVisualBodyTopYMm(item)
}

/**
 * Tope Y (mm) donde debe apoyarse la encimera (coords locales del padre, fuera del grupo scale).
 * Mismo criterio que el tope visual del mesh escalado en el visor.
 */
export function getFreeDimensionEncimeraBodyTopYMm(
  parent: Parameters<typeof getFreeDimensionVisualBodyTopYMm>[0],
): number {
  if (!parent || !usesFreeDimensionScaling(parent)) {
    return parent?.height ?? 0
  }
  return getFreeDimensionVisualBodyTopYMm(parent)
}

/** Y local (mm) de la encimera respecto al origen del padre linked. */
export function getFreeDimensionEncimeraLocalYMm(
  parent: Parameters<typeof getFreeDimensionVisualBodyTopYMm>[0],
): number {
  if (!parent) return 0
  if (!usesFreeDimensionScaling(parent)) return parent.height ?? 0
  if (isRbFreeDimensionRegleta(parent)) {
    const layout = getFreeDimensionParentLayoutAltoMm(parent) ?? 0
    const offset = getFreeDimensionAltoOffsetMm(parent)
    if (layout > 0) return layout + offset
    return parent.height ?? 0
  }
  return getFreeDimensionEncimeraBodyTopYMm(parent)
}

/** @deprecated Usar getFreeDimensionEncimeraBodyTopYMm o getFreeDimensionEncimeraLocalYMm */
export const getFreeDimensionEncimeraYInScaledGroupMm = getFreeDimensionEncimeraBodyTopYMm

export function isEncimeraLinkedElement(el: {
  descripcion?: string | null
} | null | undefined): boolean {
  return /encimera/i.test(el?.descripcion ?? '')
}

export type EncimeraConfiguratorItem = {
  idUnidades?: number
  catalogID?: string | number
  codigo?: string | number
  code?: string
  long?: string
  textures?: TexturesType
}

export function getEncimeraTextureEntry(
  item: EncimeraConfiguratorItem | null | undefined,
): TexturesType[number] | null {
  const textures = item?.textures
  if (!textures?.length) return null
  return (
    textures.find((t) => t.zone === 'Encimera' || t.zoneDisplayName === 'Encimera') ??
    textures.find((t) => /encimera/i.test(String(t.zoneDisplayName ?? t.zone ?? ''))) ??
    textures[0]
  )
}

/** idAcabadoUnidad de la unidad encimera en API (prioridad B80new). */
export function resolveEncimeraLinkedTextureIdFromZonas(
  zonas?: readonly { idAcabadoUnidad: number; zona: string }[],
): number | null {
  if (!zonas?.length) return null
  const b80 = zonas.find((z) => /b80new|b80\s*new/i.test(z.zona))
  if (b80?.idAcabadoUnidad && b80.idAcabadoUnidad > 0) return b80.idAcabadoUnidad
  const enc = zonas.find((z) => /encimera/i.test(z.zona))
  if (enc?.idAcabadoUnidad && enc.idAcabadoUnidad > 0) return enc.idAcabadoUnidad
  return null
}

export function patchEncimeraLinkedTextureIdOnItem(
  item: EncimeraConfiguratorItem | null | undefined,
  zonas?: readonly { idAcabadoUnidad: number; zona: string }[],
): number | null {
  const id = resolveEncimeraLinkedTextureIdFromZonas(zonas)
  if (!id) return null
  const tex = getEncimeraTextureEntry(item)
  if (tex) tex.linkedTextureID = id
  return id
}

/** Configurador de texturas: catálogo de la unidad encimera (idUnidades), no el del padre. */
export async function fetchEncimeraConfiguratorLinkedTextureId(
  item: EncimeraConfiguratorItem | null | undefined,
): Promise<number | null> {
  if (!item) return null
  if (item.idUnidades && item.idUnidades > 0) {
    try {
      const { API } = await import('@utils/queryBuilder')
      const resp = await API().api.acabadosGetUnidadAcabadosInfo(item.idUnidades)
      const zonas = (resp as { data?: { zonas?: { idAcabadoUnidad: number; zona: string }[] } })
        ?.data?.zonas
      const fromUnit = resolveEncimeraLinkedTextureIdFromZonas(zonas)
      if (fromUnit) {
        patchEncimeraLinkedTextureIdOnItem(item, zonas)
        return fromUnit
      }
    } catch (e) {
      console.warn('[fetchEncimeraConfiguratorLinkedTextureId]', e)
    }
  }
  const tex = getEncimeraTextureEntry(item)
  const stored = tex?.linkedTextureID
  return stored != null && stored > 0 ? stored : null
}

/** Hijo linked en escena (Encimera, Encimera B80new, typeOfSon Encimera…). */
export function isEncimeraLinkedSonItem(
  item: {
    description?: string | null
    typeOfSon?: string | null
  } | null | undefined,
): boolean {
  if (!item) return false
  if (item.typeOfSon && /encimera/i.test(String(item.typeOfSon))) return true
  return /encimera/i.test(item.description ?? '')
}

export function matchesRbRegletaPreferredEncimera(el: {
  descripcion?: string | null
  unidad?: { codigo?: string | null; referencia?: string | null }
}): boolean {
  const label = String(
    el.unidad?.codigo ?? el.unidad?.referencia ?? el.descripcion ?? '',
  ).toLowerCase()
  return label.includes('b80new')
}

/** Deja una sola encimera: prioridad Encimera B80new si viene en elementosEnlazados. */
export function resolveRbRegletaDefaultLinkedChildren<
  T extends {
    descripcion?: string | null
    unidad?: { codigo?: string | null; referencia?: string | null }
  },
>(elementos: readonly T[]): T[] {
  if (!elementos.length) return []
  const preferred = elementos.find(
    (e) => isEncimeraLinkedElement(e) && matchesRbRegletaPreferredEncimera(e),
  )
  if (preferred) {
    return [...elementos.filter((e) => !isEncimeraLinkedElement(e)), preferred]
  }
  return [...elementos]
}

/**
 * Pivot local (mm) para escalar fondo desde la pared (espalda), no desde el centro del GLB.
 * Evita que el zócalo en L se abra en la esquina al reducir fondo.
 * Sandwich: position(pivot) → scale → position(-pivot).
 */
export function getFreeDimensionFondoScalePivotLocalMm(catalogFondoMm: number): number {
  if (catalogFondoMm <= 0) return 0
  return -catalogFondoMm / 2
}

/** @deprecated Usar getFreeDimensionFondoScalePivotLocalMm */
export const getRbFreeDimensionFondoScalePivotLocalMm = getFreeDimensionFondoScalePivotLocalMm

export function getFreeDimensionFondoScalePivotMFromItem(item: {
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
} | null | undefined): number {
  const catalogRefZ = item?.freeDimensionCatalogMm?.z ?? item?.freeDimensionGlbBaseMm?.z ?? 0
  return getFreeDimensionFondoScalePivotLocalMm(catalogRefZ) / 1000
}

/** Regletas RB free-dim (sin morphs en API): encimera +100 store, ancho real ≠ cabecera ancho. */
export function isRbFreeDimensionRegleta(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return code.startsWith('RB') && usesFreeDimensionScaling(item)
}

export type FreeDimensionPlacementSource = {
  ancho?: number | null
  descripcion?: string | null
  morphs?: ReadonlyArray<{ nombre?: string; valor?: number }> | null
  elementosEnlazados?: ReadonlyArray<{
    descripcion?: string | null
    posicionY?: number | null
    unidad?: { morphs?: ReadonlyArray<{ nombre?: string; valor?: number }> | null }
  }> | null
}

function getFreeDimensionLinkedAnchoFromChildren(
  source?: FreeDimensionPlacementSource | null,
): number | null {
  if (!source?.elementosEnlazados?.length) return null
  for (const el of source.elementosEnlazados) {
    const valor = el.unidad?.morphs?.find((m) => m.nombre === 'Ancho')?.valor
    if (typeof valor === 'number' && valor > 0) return valor
  }
  return null
}

/**
 * Ancho de catálogo (mm) para escala: cabecera API (máx. modificable, p. ej. 100 en RB805).
 * Distinto del ancho activo (layout) que va en item.width.
 */
export function getFreeDimensionCatalogAnchoMm(
  backGLBInfo?: { ancho?: number | null } | null,
  layoutWidthMm?: number,
): number {
  const header = backGLBInfo?.ancho
  if (header != null && header > 0) return header
  return layoutWidthMm != null && layoutWidthMm > 0 ? layoutWidthMm : 0
}

/**
 * Ancho (mm) desde descripción tipo "Regleta Bajo 80x5" (solo si no hay cabecera `ancho` en API).
 * Primer número = alto en cm, segundo = sección en cm — no usar para ancho del padre si API trae `ancho`.
 */
export function parseFreeDimensionDescriptionAnchoMm(descripcion?: string | null): number | null {
  const desc = descripcion ?? ''
  if (!/regleta/i.test(desc)) return null
  const dimMatch = desc.match(/(\d+)\s*x\s*(\d+)/i)
  if (!dimMatch) return null
  const altoCm = Number(dimMatch[1])
  const anchoCm = Number(dimMatch[2])
  if (anchoCm > 0 && anchoCm <= 30) return anchoCm * 10
  if (altoCm > 0 && altoCm <= 30) return altoCm * 10
  return null
}

/**
 * Ancho al colocar unidades FREE_DIMENSION sin morphs en API.
 * RB805/RC…: cabecera `ancho` = lateral (100). Morph encimera hijo (50) no aplica al padre.
 */
export function resolveFreeDimensionPlacementAnchoMm(
  backGLBInfo?: FreeDimensionPlacementSource | null,
  glbBaseMm?: FreeDimensionGlbBaseMm | null,
  catalogID?: string | number,
): number {
  const headerAncho = backGLBInfo?.ancho ?? 0
  const code = catalogID != null ? String(catalogID).trim().toUpperCase() : ''

  if (
    headerAncho > 0 &&
    isFreeDimensionLinkedItem({ catalogID: code }) &&
    (code.startsWith('RB') || code.startsWith('RC'))
  ) {
    return headerAncho
  }

  const apiMorphAncho = backGLBInfo?.morphs?.find((m) => m.nombre === 'Ancho')?.valor
  if (typeof apiMorphAncho === 'number' && apiMorphAncho > 0) {
    return apiMorphAncho
  }

  const fromDesc = parseFreeDimensionDescriptionAnchoMm(backGLBInfo?.descripcion)
  if (fromDesc != null && fromDesc > 0) {
    if (headerAncho <= 0 || fromDesc <= headerAncho) return fromDesc
  }

  // Morph Ancho del hijo encimera (p. ej. 50): no usar como ancho del padre.
  const linkedAncho = getFreeDimensionLinkedAnchoFromChildren(backGLBInfo)
  if (
    linkedAncho != null &&
    linkedAncho > 0 &&
    !(code.startsWith('RB') || code.startsWith('RC'))
  ) {
    if (headerAncho <= 0 || linkedAncho <= headerAncho) return linkedAncho
  }

  const bboxX = glbBaseMm?.x ?? 0
  if (bboxX > 0) {
    if (headerAncho <= 0) return bboxX
    if (bboxX < headerAncho - 0.5) return bboxX
  }

  if (headerAncho > 0) return headerAncho
  return linkedAncho ?? fromDesc ?? 0
}

export type EncimeraCatalogMm = { x: number; y: number; z: number }

/** Tamaño catálogo API del GLB encimera (p. ej. B80new: 800×620), distinto del width/thickness en store. */
export function getEncimeraCatalogMm(
  encimera: {
    width?: number
    thickness?: number
    height?: number
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
): EncimeraCatalogMm | null {
  const stored = encimera?.encimeraCatalogMm
  if (stored && stored.x > 0 && stored.z > 0) return stored
  const gx = encimera?.glbSize?.x
  const gz = encimera?.glbSize?.z
  if (gx != null && gz != null && gx > 0 && gz > 0) {
    return {
      x: Math.round(gx * 1000),
      y: Math.round((encimera?.glbSize?.y ?? 0) * 1000),
      z: Math.round(gz * 1000),
    }
  }
  return null
}

/** Ancho catálogo de referencia del hijo encimera (API/GLB, p. ej. B80new 800 mm). */
export function getEncimeraAnchoCatalogBaselineMm(
  encimera: {
    width?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
): number {
  const fromApi = getEncimeraCatalogMm(encimera)?.x
  if (fromApi != null && fromApi > 0) return fromApi
  const anchoMorph = encimera?.morphs?.find((m) => m.name === 'Ancho')
  const fromRef = Number(anchoMorph?._default ?? 0)
  if (fromRef > 0) return fromRef
  return Number(encimera?.width ?? 0)
}

/**
 * Dimensiona la encimera según el padre y el catálogo inicial:
 * - enc &lt; padre (p. ej. 50 vs 100): proporcional (padre 200 → enc 100).
 * - enc &gt; padre (p. ej. 620 vs 600): delta fijo (+20 → padre 500 → enc 520).
 */
export function computeEncimeraLinkedDimensionFromParent(
  parentLayoutMm: number,
  encCatalogBaselineMm: number,
  parentCatalogMm: number,
): number {
  if (parentLayoutMm <= 0) return 0
  if (encCatalogBaselineMm <= 0 || parentCatalogMm <= 0) return parentLayoutMm
  if (encCatalogBaselineMm < parentCatalogMm * 0.9) {
    return Math.round((parentLayoutMm * encCatalogBaselineMm) / parentCatalogMm)
  }
  return parentLayoutMm + (encCatalogBaselineMm - parentCatalogMm)
}

/** Free-dim con hijo encimera: ancho según catálogo (proporcional o delta), sin scaleX del grupo. */
export function syncFreeDimensionEncimeraAnchoToParent(
  encimera: { width?: number; morphs?: Morphs[] },
  parent: {
    width?: number
    thickness?: number
    morphs?: Morphs[]
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  },
): void {
  if (!usesFreeDimensionScaling(parent)) return
  if (freeDimensionUsesLb8060StyleAltoOffset(parent)) {
    syncLb8060EncimeraToParent(encimera, parent)
    return
  }
  const parentW = parent.width ?? 0
  if (parentW <= 0) return
  const anchoMorph = encimera.morphs?.find((m) => m.name === 'Ancho')

  // RB805 + Encimera B80new (sin morph Ancho): ancho = lateral del padre (100 mm).
  if (isRbFreeDimensionRegleta(parent) && !encimeraUsesAnchoMorphTarget(encimera)) {
    encimera.width = parentW
    if (anchoMorph) anchoMorph.default = parentW
    return
  }

  const encBaseline = getEncimeraAnchoCatalogBaselineMm(encimera)
  const parentCatalog =
    parent.freeDimensionCatalogMm?.x ??
    Number(parent.morphs?.find((m) => m.name === 'Ancho')?.default ?? parentW)

  let encW: number
  if (encBaseline > 0 && parentCatalog > 0) {
    encW = computeEncimeraLinkedDimensionFromParent(parentW, encBaseline, parentCatalog)
  } else {
    encW = parentW
    if (anchoMorph) {
      const morphMax = Number(anchoMorph.max ?? 0)
      const morphMin = Number(anchoMorph.min ?? 0)
      if (morphMax > morphMin && morphMax > 0) {
        encW = Math.min(parentW, morphMax)
      }
    }
  }

  if (anchoMorph) {
    const morphMax = Number(anchoMorph.max ?? 0)
    const morphMin = Number(anchoMorph.min ?? 0)
    if (morphMax > morphMin && morphMax > 0) {
      encW = Math.min(Math.max(encW, morphMin), morphMax)
    }
  }

  if (encW <= 0) return
  encimera.width = encW
  if (anchoMorph) {
    anchoMorph.default = encW
  }
}

/** Encimera con morph Ancho en el GLB. */
export function encimeraUsesAnchoMorphTarget(
  encimera: { morphs?: Morphs[] } | null | undefined,
): boolean {
  const morph = encimera?.morphs?.find((m) => m.name === 'Ancho')
  if (!morph) return false
  const max = Number(morph.max ?? 0)
  const min = Number(morph.min ?? 0)
  return max > min
}

/**
 * Escala X del grupo encimera. Con morph Ancho en el GLB → siempre 1 (deformar mesh, no estirar).
 * RB805: padre 100 mm, encimera morph 50 mm → dibujar a 50, no scaleX=2.
 */
export function getFreeDimensionEncimeraAnchoScaleX(
  encimera: {
    width?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  },
  parent?: {
    width?: number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
  } | null,
): number {
  if (encimeraUsesAnchoMorphTarget(encimera)) return 1
  const targetW = encimera.width ?? parent?.width ?? 0
  if (targetW <= 0) return 1
  const base = getEncimeraAnchoCatalogBaselineMm(encimera)
  if (base > 0) return targetW / base
  const morph = encimera.morphs?.find((m) => m.name === 'Ancho')
  if (!morph) return 1
  const fromMorph = Number(morph._default ?? morph.default ?? 0)
  return fromMorph > 0 ? targetW / fromMorph : 1
}

/** Encimera con morph target Fondo en el GLB (deformar mesh, no scale Z del grupo). */
export function encimeraUsesFondoMorphTarget(
  encimera: { morphs?: Morphs[] } | null | undefined,
): boolean {
  const morph = encimera?.morphs?.find((m) => m.name === 'Fondo')
  if (!morph) return false
  const max = Number(morph.max ?? 0)
  const min = Number(morph.min ?? 0)
  return max > min
}

/** Fondo catálogo de referencia del hijo encimera (p. ej. B80new 620 mm). */
export function getEncimeraFondoCatalogBaselineMm(
  encimera: {
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
): number {
  const fromApi = getEncimeraCatalogMm(encimera)?.z
  if (fromApi != null && fromApi > 0) return fromApi
  const fondoMorph = encimera?.morphs?.find((m) => m.name === 'Fondo')
  const fromMax = Number(fondoMorph?.max ?? 0)
  if (fromMax > 0) return fromMax
  const fromRef = Number(fondoMorph?._default ?? 0)
  if (fromRef > 0) return fromRef
  return Number(encimera?.thickness ?? 0)
}

/** Delta fijo encimera vs lateral (p. ej. 620 − 600 = 20 mm). */
export function getEncimeraFondoDeltaMm(
  encimera: { thickness?: number; morphs?: Morphs[] },
  parent: {
    thickness?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  },
): number {
  const encBaseline = getEncimeraFondoCatalogBaselineMm(encimera)
  const parentCatalog =
    parent.freeDimensionCatalogMm?.z ??
    Number(parent.morphs?.find((m) => m.name === 'Fondo')?.default ?? parent.thickness ?? 0)
  if (encBaseline > 0 && parentCatalog > 0) return encBaseline - parentCatalog
  return encBaseline > 0 ? Math.max(0, encBaseline - 600) : 0
}

/**
 * Fondo encimera = fondo padre + delta catálogo (p. ej. padre 600 → 620; padre 500 → 520).
 */
export function syncFreeDimensionEncimeraFondoToParent(
  encimera: {
    thickness?: number
    morphs?: Morphs[]
    z?: number
    matrix?: { elements?: number[] }
  },
  parent: {
    thickness?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  },
): void {
  if (!usesFreeDimensionScaling(parent) && !freeDimensionUsesLb8060StyleAltoOffset(parent)) {
    return
  }
  const parentFondo = parent.thickness ?? parent.freeDimensionCatalogMm?.z ?? 0
  if (parentFondo <= 0) {
    applyLb8060EncimeraCatalogFondo(encimera)
    return
  }
  const encBaseline = getEncimeraFondoCatalogBaselineMm(encimera)
  const parentCatalog =
    parent.freeDimensionCatalogMm?.z ??
    Number(parent.morphs?.find((m) => m.name === 'Fondo')?.default ?? parentFondo)
  const target =
    encBaseline > 0 && parentCatalog > 0
      ? computeEncimeraLinkedDimensionFromParent(parentFondo, encBaseline, parentCatalog)
      : parentFondo + getEncimeraFondoDeltaMm(encimera, parent)
  if (target <= 0) return
  encimera.thickness = target
  const fondoMorph = encimera.morphs?.find((m) => m.name === 'Fondo')
  if (fondoMorph) {
    fondoMorph.default = target
    // max/min sin tocar: handleNodeMorphs deforma el mesh; evita scale Z del grupo (pivote incorrecto).
  }
  if (isRbFreeDimensionRegleta(parent)) {
    encimera.z = 0
    syncLinkedSonLocalMatrixTranslation(encimera)
  }
  logFreeDimensionEncimeraFondo('syncFondoToParent', encimera, parent, {
    targetThickness: target,
    parentCatalogZ: parentCatalog,
    encBaseline,
  })
}

/** Z local (mm) del hijo encimera: valor de store/API (p. ej. 0), sin corrección por delta de fondo. */
export function getFreeDimensionEncimeraLocalZMm(
  encimera: { z?: number } | null | undefined,
  _parent?: unknown,
): number {
  return encimera?.z ?? 0
}

/** Escala Z encimera según fondo catálogo hijo (620), no fondo padre (600). */
export function getFreeDimensionEncimeraFondoScaleZ(
  encimera: {
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
  parent?: {
    thickness?: number
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  } | null,
): number {
  if (!encimera) return 1
  if (encimeraUsesFondoMorphTarget(encimera)) return 1
  return getLb8060EncimeraFondoMeshScale(encimera)
}

/** @deprecated Usar syncFreeDimensionEncimeraAnchoToParent */
export const syncRbRegletaEncimeraAnchoToParent = syncFreeDimensionEncimeraAnchoToParent

/** Unidades con offset de alto (+100 RB/RC, CC/RA): escala por layout, no solo store/scaleBase. */
export function usesFreeDimensionAltoOffsetLayoutScale(
  item?: { catalogID?: string | number; codigo?: string | number; hasMorphs?: boolean } | null,
): boolean {
  return usesFreeDimensionScaling(item) && getFreeDimensionAltoOffsetMm(item) !== 0
}

/**
 * Escala Y para que el tope del cuerpo (sidebar) coincida con catálogo al colocar y al redimensionar.
 * layout×(cat+offset)/(scaleBase×cat). Aplica a LA, CC, LB8060, etc.
 */
export function getFreeDimensionLayoutAltoScaleY(
  layoutMm: number,
  catalogY: number,
  scaleBaseY: number,
  offsetMm: number,
): number {
  if (layoutMm <= 0 || scaleBaseY <= 0 || catalogY <= 0) return 1
  return (layoutMm * (catalogY + offsetMm)) / (scaleBaseY * catalogY)
}

/** @deprecated Usar getFreeDimensionAltoGroupScaleY */
export function getRbFreeDimensionAltoScaleY(
  item: Parameters<typeof getFreeDimensionAltoGroupScaleY>[0],
): number {
  return getFreeDimensionAltoGroupScaleY(item)
}

/** @deprecated Usar getFreeDimensionLayoutAltoScaleY */
export function getFreeDimensionLb8060LayoutScaleY(
  layoutMm: number,
  catalogY: number,
  scaleBaseY: number,
  offsetMm = 0,
): number {
  return getFreeDimensionLayoutAltoScaleY(layoutMm, catalogY, scaleBaseY, offsetMm)
}

const FREE_DIMENSION_DRAW_DEBUG_LA =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_FREE_DIM_DEBUG_LA === 'true'

/** Activar solo con VITE_FREE_DIM_DEBUG_ANCHO=true en .env.local */
const FREE_DIMENSION_DRAW_DEBUG_ANCHO =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_FREE_DIM_DEBUG_ANCHO === 'true'

/** Activar solo con VITE_FREE_DIM_DEBUG_FONDO=true en .env.local */
const FREE_DIMENSION_DRAW_DEBUG_FONDO =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_FREE_DIM_DEBUG_FONDO === 'true'

/** Activar solo con VITE_FREE_DIM_DEBUG_MATERIAL=true en .env.local */
const FREE_DIMENSION_DRAW_DEBUG_MATERIAL =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_FREE_DIM_DEBUG_MATERIAL === 'true'

/** Activar con VITE_FREE_DIM_DEBUG_ZOCALO=true */
const FREE_DIMENSION_DRAW_DEBUG_ZOCALO =
  typeof import.meta !== 'undefined' &&
  (import.meta.env?.VITE_FREE_DIM_DEBUG_ZOCALO === 'true' ||
    import.meta.env?.VITE_FREE_DIM_DEBUG_FONDO === 'true')

function isFreeDimensionZocaloMeshDebugEnabled(): boolean {
  return FREE_DIMENSION_DRAW_DEBUG_ZOCALO
}

/**
 * Mapeo dimensiones (igual que MorphDictionary / universales):
 * - Ancho  → item.width
 * - Alto   → item.height (en FREE_DIMENSION con offset: sidebar = height − offset)
 * - Fondo  → item.thickness
 */
export const FREE_DIMENSION_FIELD_BY_MORPH = {
  Ancho: 'width',
  Alto: 'height',
  Fondo: 'thickness',
} as const

/** Log colocación Ancho/Alto/Fondo (DEV). Filtrar: [FreeDim place] */
export function logFreeDimensionPlacement(
  phase: string,
  catalogID: string | number | undefined,
  data: {
    morphs: Morphs[]
    width: number
    heightStore: number
    thickness: number
    catalogMm?: FreeDimensionCatalogMm
    glbBaseMm?: FreeDimensionGlbBaseMm
    altoOffsetMm?: number
  },
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_ANCHO && !FREE_DIMENSION_DRAW_DEBUG_FONDO) return
  const code = getLinkedItemCatalogCode({ catalogID })
  if (!code || !isFreeDimensionLinkedItem({ catalogID: code })) return
  const pick = (name: string) => data.morphs.find((m) => m.name === name)
  const ancho = pick('Ancho')
  const alto = pick('Alto')
  const fondo = pick('Fondo')
  const sidebarAlto =
    data.altoOffsetMm != null ? data.heightStore - data.altoOffsetMm : data.heightStore
  const anchoViaMorph =
    data.glbBaseMm && data.catalogMm
      ? resolveFreeDimensionAnchoViaGlbMorph(data.glbBaseMm, data.catalogMm, data.width)
      : false
  console.log('[FreeDim place]', phase, code, {
    anchoViaGlbMorph: anchoViaMorph,
    ancho: {
      sidebarMm: data.width,
      itemField: `width=${data.width}`,
      morph: ancho ? { refGlbMm: ancho.default, activeMm: ancho._default, scale: (ancho._default ?? 0) / (ancho.default || 1) } : null,
    },
    alto: {
      sidebarMm: sidebarAlto,
      storeHeightMm: data.heightStore,
      itemField: `height=${data.heightStore}`,
      altoOffsetMm: data.altoOffsetMm ?? 0,
      morph: alto ? { refGlbMm: alto.default, activeMm: alto._default, scale: (alto._default ?? 0) / (alto.default || 1) } : null,
    },
    fondo: {
      sidebarMm: data.thickness,
      itemField: `thickness=${data.thickness}`,
      morph: fondo ? { refGlbMm: fondo.default, activeMm: fondo._default, scale: (fondo._default ?? 0) / (fondo.default || 1) } : null,
    },
    catalogMm: data.catalogMm,
    glbBaseMm: data.glbBaseMm,
  })
}

/**
 * Material zona Puerta al colocar / heredar texturas (DEV).
 * Filtrar consola: `[FreeDim material Puerta]`
 */
export function logFreeDimensionPuertaMaterial(item: {
  id?: string
  catalogID?: string | number
  type?: string
}): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_MATERIAL || !item.id || item.type !== 'linkedItem') return
  void import('@utils/debug/compareLinkedItemMaterials').then((mod) => {
    mod.logPuertaMaterial(item.id!)
  })
}

/**
 * Material zona Zócalo al colocar / heredar texturas (DEV).
 * Filtrar consola: `[FreeDim material Zocalo]`
 * Activar: VITE_FREE_DIM_DEBUG_MATERIAL=true en .env.local
 */
export function logFreeDimensionZocaloMaterial(item: {
  id?: string
  catalogID?: string | number
  type?: string
}): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_MATERIAL || !item.id || item.type !== 'linkedItem') return
  void import('@utils/debug/compareLinkedItemMaterials').then((mod) => {
    mod.logZocaloMaterial(item.id!)
  })
}

export function logFreeDimensionZocaloMaterialRuntime(
  itemId: string,
  runtime: import('@utils/debug/compareLinkedItemMaterials').ZocaloRenderRuntimeContext,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_MATERIAL && !isFreeDimensionZocaloMeshDebugEnabled()) return
  void import('@utils/debug/compareLinkedItemMaterials').then((mod) => {
    mod.logZocaloMaterial(itemId, runtime)
  })
}

export type FreeDimensionZocaloMeshLogEntry = {
  meshName: string
  zoneKey: string | null
  localPositionMm: { x: number; y: number; z: number }
  localScale: { x: number; y: number; z: number }
  localBoxMm: { minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number }
  sizeMm: { x: number; y: number; z: number }
}

/**
 * Posición/escala/bbox de cada mesh Zócalo en el GLB clonado (antes del grupo scale del visor).
 * Filtrar consola: `[FreeDim zocalo mesh]`
 * Activar: DEV automático, o VITE_FREE_DIM_DEBUG_ZOCALO=true en .env.local
 */
export function logFreeDimensionZocaloMeshPositions(
  phase: string,
  itemId: string,
  root: Object3D,
  item: {
    catalogID?: string | number
    width?: number
    thickness?: number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
  },
  groupScale?: { x: number; y: number; z: number },
  zones?: Zone,
  textures?: Textures,
): void {
  if (!isFreeDimensionZocaloMeshDebugEnabled()) return
  const code = getLinkedItemCatalogCode(item)
  const entries: FreeDimensionZocaloMeshLogEntry[] = []

  root.updateMatrixWorld(true)
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return
    const mesh = obj as Mesh
    const zoneKey =
      zones && mesh.name in zones
        ? String(mesh.name)
        : Object.keys(zones ?? {}).find((k) => zones![k].name === mesh.name) ?? null
    const zone = zoneKey && zones ? zones[zoneKey as keyof Zone] : undefined
    const tex = textures?.find((t) => t.zoneName === mesh.name)
    if (!isZocaloZone(zone, tex as never) && !/zocalo/i.test(mesh.name)) return

    const local = mesh.position
    const scl = mesh.scale
    const box = new Box3().setFromObject(mesh)
    const size = box.getSize(new Vector3())
    const toMm = (v: number) => Math.round(v * 1000)

    entries.push({
      meshName: mesh.name,
      zoneKey,
      localPositionMm: { x: toMm(local.x), y: toMm(local.y), z: toMm(local.z) },
      localScale: {
        x: Math.round(scl.x * 1000) / 1000,
        y: Math.round(scl.y * 1000) / 1000,
        z: Math.round(scl.z * 1000) / 1000,
      },
      localBoxMm: {
        minX: toMm(box.min.x),
        minY: toMm(box.min.y),
        minZ: toMm(box.min.z),
        maxX: toMm(box.max.x),
        maxY: toMm(box.max.y),
        maxZ: toMm(box.max.z),
      },
      sizeMm: { x: toMm(size.x), y: toMm(size.y), z: toMm(size.z) },
    })
  })

  console.warn('[FreeDim zocalo mesh]', phase, code, itemId, {
    hint: 'Filtra consola por [FreeDim zocalo mesh]. Forzar: VITE_FREE_DIM_DEBUG_ZOCALO=true',
    itemLayout: {
      width: item.width,
      thickness: item.thickness,
      catalogMm: item.freeDimensionCatalogMm,
      glbBaseMm: item.freeDimensionGlbBaseMm,
      scaleBaseMm: item.freeDimensionScaleBaseMm,
    },
    groupScaleVector: groupScale,
    scaleZ: groupScale?.z,
    fondoGroupScaleZ: getFreeDimensionFondoGroupScaleZ(item),
    meshCount: entries.length,
    meshes: entries,
  })
}

/** Log ancho/escala free-dim (DEV). Filtrar: [FreeDim ancho] */
export function logFreeDimensionAncho(
  phase: string,
  item: {
    catalogID?: string | number
    width?: number
    height?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
    hasMorphs?: boolean
  } | null | undefined,
  extra?: Record<string, unknown>,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_ANCHO || !item) return
  const code = getLinkedItemCatalogCode(item)
  if (!usesFreeDimensionScaling(item)) return
  const anchoMorph = item.morphs?.find((m) => m.name === 'Ancho')
  const morphViaGlb = itemUsesFreeDimensionAnchoGlbMorph(item)
  const itemW = item.width ?? 0
  const morphRefX = anchoMorph?.default ?? 0
  const morphActiveX = anchoMorph?._default ?? itemW
  const morphMaxMm = getFreeDimensionAnchoMorphMaxMm(anchoMorph, item.freeDimensionCatalogMm)
  const extraScaleX = morphViaGlb
    ? getFreeDimensionAnchoGlbMorphScaleX(itemW, anchoMorph, item.freeDimensionCatalogMm)
    : 1
  const scaleRatioX = morphViaGlb
    ? extraScaleX
    : morphRefX > 0
      ? morphActiveX / morphRefX
      : 1
  const glbBaseX = item.freeDimensionGlbBaseMm?.x ?? 0
  const visualAnchoEstMm = morphViaGlb
    ? morphMaxMm * extraScaleX
    : glbBaseX > 0
      ? glbBaseX * scaleRatioX
      : morphActiveX
  console.log('[FreeDim ancho]', phase, code, {
    itemWidth: itemW,
    layoutWidthMm: extra?.layoutWidthMm ?? morphActiveX,
    catalogAnchoMm: item.freeDimensionCatalogMm?.x,
    glbBaseX,
    scaleBaseX: item.freeDimensionScaleBaseMm?.x,
    morphAnchoMaxMm: morphMaxMm,
    morphAnchoForMeshMm: morphRefX,
    morphAnchoTargetMm: morphActiveX,
    extraScaleX,
    scaleRatioX,
    visualAnchoEstMm: Math.round(visualAnchoEstMm),
    widthVia: morphViaGlb
      ? 'morph GLB hasta max + scaleX si ancho > max'
      : 'universal (_default activo / default ref GLB)',
    ...extra,
  })
}

/** Log fondo/escala Z (DEV). Filtrar consola: `[FreeDim fondo]` — activar VITE_FREE_DIM_DEBUG_FONDO=true */
export function logFreeDimensionFondo(
  phase: string,
  item: {
    catalogID?: string | number
    codigo?: string | number
    thickness?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
    hasMorphs?: boolean
  } | null | undefined,
  extra?: Record<string, unknown>,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_FONDO || !item) return
  const code = getLinkedItemCatalogCode(item)
  const fondoMorph = item.morphs?.find((m) => m.name === 'Fondo')
  const active = item.thickness ?? fondoMorph?._default ?? 0
  const morphRef = fondoMorph?.default ?? 0
  const glbZ = item.freeDimensionGlbBaseMm?.z ?? 0
  const catalogZ = item.freeDimensionCatalogMm?.z ?? 0
  const scaleBaseZ = item.freeDimensionScaleBaseMm?.z ?? 0
  const universalScaleZ = getFreeDimensionFondoGroupScaleZ(item, fondoMorph)
  const ratioFromScaleBase =
    scaleBaseZ > 0 && active > 0 ? active / scaleBaseZ : 1
  const fondoViaMorph = itemUsesFreeDimensionFondoGlbMorph(item)
  const isRb = isRbFreeDimensionRegleta(item)
  const isUniversalMesh = Boolean(item.hasMorphs && !usesFreeDimensionScaling(item))
  console.log('[FreeDim fondo]', phase, code, {
    isRbRegleta: isRb,
    hasMorphsApi: item.hasMorphs,
    usesFreeDimScaling: usesFreeDimensionScaling(item),
    universalMeshMorph: isUniversalMesh,
    fondoViaGlbMorph: fondoViaMorph,
    sidebarThicknessMm: active,
    morphRefGlbMm: morphRef,
    morphActiveMm: fondoMorph?._default,
    catalogFondoMm: catalogZ,
    glbBBoxZMm: glbZ,
    scaleBaseZMm: scaleBaseZ,
    scaleZ_universalFormula: universalScaleZ,
    scaleZ_fromScaleBase: ratioFromScaleBase,
    scaleMismatch: Math.abs(universalScaleZ - ratioFromScaleBase) > 0.001,
    visualFondoEstMm: Math.round(glbZ * universalScaleZ),
    widthVia: fondoViaMorph
      ? 'morph GLB fondo'
      : isUniversalMesh
        ? 'morph malla API (scale grupo 1)'
        : 'grupo scaleZ = thickness / refGlbZ',
    ...extra,
  })
}

/** Log encimera fondo (DEV). Filtrar: `[FreeDim encimera fondo]` */
export function logFreeDimensionEncimeraFondo(
  phase: string,
  encimera: {
    catalogID?: string | number
    description?: string
    thickness?: number
    width?: number
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
    morphs?: Morphs[]
    z?: number
  } | null | undefined,
  parent: {
    catalogID?: string | number
    thickness?: number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  } | null | undefined,
  extra?: Record<string, unknown>,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_FONDO || !encimera) return
  const encCatalog = getEncimeraCatalogMm(encimera)
  const baseZ = getEncimeraFondoCatalogBaselineMm(encimera)
  const targetZ = encimera.thickness ?? 0
  const meshScaleZ = getLb8060EncimeraFondoMeshScale(encimera)
  const parentFondo = parent?.thickness ?? parent?.freeDimensionCatalogMm?.z ?? 0
  const parentScaleZ =
    parent && usesFreeDimensionScaling(parent)
      ? getFreeDimensionFondoGroupScaleZ(parent)
      : null
  console.log('[FreeDim encimera fondo]', phase, encimera.catalogID ?? encimera.description, {
    parentCode: getLinkedItemCatalogCode(parent ?? undefined),
    parentFondoMm: parentFondo,
    parentScaleZ,
    encThicknessStoreMm: targetZ,
    encCatalogMm: encCatalog,
    encCatalogBaselineZMm: baseZ,
    encGlbSizeZMm: encimera.glbSize?.z != null ? Math.round(encimera.glbSize.z * 1000) : null,
    meshScaleZ: meshScaleZ,
    visualFondoEstMm: Math.round(baseZ * meshScaleZ),
    localZMm: encimera.z,
    usesFondoMorph: encimeraUsesFondoMorphTarget(encimera),
    ...extra,
  })
}

/** Log al colocar unidades LA (solo DEV). Filtrar consola: [FreeDim draw LA] */
export function logFreeDimensionDrawLA(
  phase: string,
  catalogID: string | number | undefined,
  data: Record<string, unknown>,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_LA) return
  const code = getLinkedItemCatalogCode({ catalogID })
  if (!code.startsWith('LA')) return
  console.log('[FreeDim draw LA]', phase, code, data)
}

/** Códigos LA (columna alta, sin encimera). */
export function isLaFreeDimensionColumn(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return code.startsWith('LA') && usesFreeDimensionScaling(item)
}

/** Códigos LC (incluye LC23060 con offset −100). */
export function isLcFreeDimensionColumn(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return code.startsWith('LC') && usesFreeDimensionScaling(item)
}

/** Columnas CC (CC14060…CC24060): +100 en store y escala con offset explícito. */
export function isCcFreeDimensionColumn(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (
    (FREE_DIMENSION_CC_COLUMN_CATALOG_IDS as readonly string[]).includes(code) &&
    usesFreeDimensionScaling(item)
  )
}

/** Columnas RA (RA805…RA925): +100 en store y escala con offset explícito. */
export function isRaFreeDimensionColumn(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (
    (FREE_DIMENSION_RA_COLUMN_CATALOG_IDS as readonly string[]).includes(code) &&
    usesFreeDimensionScaling(item)
  )
}

/** Alto (mm) para escala Y / OBB (item.height; offset ±100 solo RB/RC/CC/RA). */
export function getFreeDimensionAltoScaleTargetMm(
  item: {
    height?: number
    catalogID?: string | number
    codigo?: string | number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    hasMorphs?: boolean
    morphs?: Morphs[]
  } | null | undefined,
): number {
  if (!usesFreeDimensionScaling(item)) return item?.height ?? 0
  if (itemUsesFreeDimensionAltoApiMorph(item)) {
    const alto = item?.morphs?.find((m) => m.name === 'Alto')
    const h = item?.height ?? alto?._default ?? alto?.default ?? 0
    return h > 0 ? clampMorphMm(h, alto?.min, alto?.max) : 0
  }
  if (isFreeDimensionTpcNoMorphCatalog(item)) {
    return getFreeDimensionTpcNoMorphLayoutAltoMm(item)
  }
  return item?.height ?? item?.freeDimensionCatalogMm?.y ?? 0
}

/**
 * Alto del cuerpo del mueble (sidebar / catálogo) para posicionar hijos.
 * storeHeight − offset (+100 RB/RC/CC/RA, −100 LC23060; laterales/LB8060: 0).
 */
export function getFreeDimensionParentLayoutAltoMm(
  item: {
    catalogID?: string | number
    codigo?: string | number
    height?: number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  } | null | undefined,
): number | null {
  if (!usesFreeDimensionScaling(item)) return null
  const body =
    getFreeDimensionSidebarAlto(item?.height, item) ||
    item?.freeDimensionCatalogMm?.y ||
    0
  return body > 0 ? body : null
}

/**
 * Grosor de encimera (mm) según acabado. Solo afecta al morph Alto del hijo, no a su posición Y.
 */
export function getEncimeraAcabadoThicknessMm(acabado?: string | null): number | null {
  if (!acabado) return null
  const a = acabado.toLowerCase()
  if (a.includes('porcelanicas 12')) return 12
  if (a.includes('porcelanicas 20')) return 20
  if (a.includes('postformadas')) return 30
  if (a.includes('compacto')) return 10
  return null
}

/** @deprecated Usar getFreeDimensionVisualBodyTopYMm (cuerpo) o getFreeDimensionEncimeraTopStoreYMm (encimera). */
export const getFreeDimensionEncimeraTopYMm = getFreeDimensionVisualBodyTopYMm

/** Tope Y store (mm): sidebar + zócalo (+100 RB/RC…). Encimera en tope visual del cuerpo. */
export function getFreeDimensionEncimeraTopStoreYMm(
  parent: {
    height?: number
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  } | null | undefined,
): number {
  if (!usesFreeDimensionScaling(parent)) return parent?.height ?? 0
  if (parent?.height != null && parent.height > 0) return parent.height
  const sidebar =
    getFreeDimensionParentLayoutAltoMm(parent) ?? parent?.freeDimensionCatalogMm?.y ?? 0
  return sidebar > 0 ? getFreeDimensionStoreAlto(sidebar, parent) : 0
}

/** Y en store (mm): origen padre + tope local del mesh escalado. */
export function getFreeDimensionEncimeraPositionYMm(
  parent: {
    y?: number
    height?: number
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    morphs?: Morphs[]
  } | null | undefined,
): number {
  if (!parent) return 0
  const localTop = usesFreeDimensionScaling(parent)
    ? getFreeDimensionEncimeraLocalYMm(parent)
    : (parent.height ?? 0)
  return (parent.y ?? 0) + localTop
}

/**
 * Restaura el grosor de la encimera según acabado (morph Alto). No modifica posición ni ancho/fondo.
 */
export function applyEncimeraThicknessMm(encimera: {
  height?: number
  morphs?: Morphs[]
  textures?: { acabado?: string | null }[]
}): number {
  const morphAlto = encimera.morphs?.find((m) => m.name === 'Alto')
  const fromAcabado = getEncimeraAcabadoThicknessMm(encimera.textures?.[0]?.acabado)
  const glbBase =
    morphAlto?._default != null && morphAlto._default > 0 && morphAlto._default <= 50
      ? morphAlto._default
      : morphAlto?.min != null && morphAlto.min > 0 && morphAlto.min <= 50
        ? morphAlto.min
        : null
  const thickness = fromAcabado ?? glbBase ?? (encimera.height != null && encimera.height <= 50 ? encimera.height : 10)

  encimera.height = thickness
  if (morphAlto) {
    morphAlto.default = thickness
    morphAlto.value = thickness
  }
  return thickness
}

/** @deprecated Preferir getEncimeraFondoCatalogBaselineMm */
export function getLb8060EncimeraCatalogFondoMm(
  encimera: { thickness?: number; morphs?: Morphs[] } | null | undefined,
): number {
  return getEncimeraFondoCatalogBaselineMm(encimera)
}

export function applyLb8060EncimeraCatalogFondo(encimera: {
  thickness?: number
  morphs?: Morphs[]
}): number {
  const fondo = getLb8060EncimeraCatalogFondoMm(encimera)
  if (fondo <= 0) return encimera.thickness ?? 0
  encimera.thickness = fondo
  const fondoMorph = encimera.morphs?.find((m) => m.name === 'Fondo')
  if (fondoMorph) fondoMorph.default = fondo
  return fondo
}

/** Escala Z del mesh solo si el GLB no tiene morph Fondo (sin morph → scale grupo). */
export function getLb8060EncimeraFondoMeshScale(
  encimera: { thickness?: number; morphs?: Morphs[] } | null | undefined,
): number {
  if (encimeraUsesFondoMorphTarget(encimera)) return 1
  const fondoMorph = encimera?.morphs?.find((m) => m.name === 'Fondo')
  const base = getEncimeraFondoCatalogBaselineMm(encimera)
  const target = Number(encimera?.thickness ?? fondoMorph?.default ?? base)
  if (base <= 0 || target <= 0) return 1
  return target / base
}

/** Sincroniza la traslación local X/Y/Z del hijo en su matrix (p. ej. encimera tras cambiar Alto). */
export function syncLinkedSonLocalMatrixTranslation(son: {
  matrix?: { elements?: number[] }
  x?: number
  y?: number
  z?: number
}): void {
  const el = son.matrix?.elements
  if (!el || el.length < 16) return
  el[12] = son.x ?? 0
  el[13] = son.y ?? 0
  el[14] = son.z ?? 0
}

/**
 * LB8060/CB9060: ancho = lateral (19 mm); fondo = catálogo encimera (620 mm, no 600 del lateral).
 */
export function syncLb8060EncimeraToParent(
  encimera: { width?: number; thickness?: number; morphs?: Morphs[] },
  parent: { width?: number; thickness?: number; catalogID?: string | number; codigo?: string | number },
): void {
  if (!freeDimensionUsesLb8060StyleAltoOffset(parent)) return
  const w = parent.width ?? encimera.width ?? 0
  if (w > 0) encimera.width = w
  for (const m of encimera.morphs ?? []) {
    if (m.name === 'Ancho' && w > 0) m.default = w
  }
  syncFreeDimensionEncimeraFondoToParent(encimera, parent)
}

/** Sincroniza ancho/fondo encimera con el padre free-dim (RB, LB8060, etc.). */
export function syncFreeDimensionEncimeraToParent(
  encimera: {
    y?: number
    width?: number
    thickness?: number
    morphs?: Morphs[]
  },
  parent: Parameters<typeof syncFreeDimensionEncimeraAnchoToParent>[1],
): void {
  syncLb8060EncimeraToParent(encimera, parent)
  syncFreeDimensionEncimeraAnchoToParent(encimera, parent)
  syncFreeDimensionEncimeraFondoToParent(encimera, parent)
}

/** Posiciona la encimera sobre el borde superior del padre; LB8060/CB9060 también iguala ancho/fondo. */
export function placeLinkedEncimeraY(
  encimera: {
    y?: number
    width?: number
    thickness?: number
    morphs?: Morphs[]
    matrix?: { elements?: number[] }
    x?: number
    z?: number
  },
  parent: {
    y?: number
    height?: number
    width?: number
    thickness?: number
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  },
  options?: { backendPosicionY?: number; backendPosicionZ?: number },
): number {
  const backendZ = options?.backendPosicionZ
  if (typeof backendZ === 'number') {
    encimera.z = backendZ
  } else if (encimera.z == null || encimera.z === undefined) {
    encimera.z = 0
  }

  let y: number
  if (usesFreeDimensionScaling(parent)) {
    const calculated = getFreeDimensionEncimeraLocalYMm(parent)
    const backendY = options?.backendPosicionY
    if (
      typeof backendY === 'number' &&
      backendY > 0 &&
      Math.abs(backendY - calculated) <= 2
    ) {
      y = backendY
    } else {
      y = calculated
    }
  } else {
    y = (parent.y ?? 0) + (parent.height ?? 0)
  }
  encimera.y = y
  if (isRbFreeDimensionRegleta(parent)) {
    applyEncimeraThicknessMm(encimera)
    syncFreeDimensionEncimeraAnchoToParent(encimera, parent)
    syncFreeDimensionEncimeraFondoToParent(encimera, parent)
  } else {
    syncFreeDimensionEncimeraToParent(encimera, parent)
  }
  syncLinkedSonLocalMatrixTranslation(encimera)
  return y
}

export function isFreeDimensionDimensionMorph(name: string): boolean {
  return (DIMENSION_MORPH_NAMES as readonly string[]).includes(
    name as (typeof DIMENSION_MORPH_NAMES)[number],
  )
}

export function vector3ToFreeDimensionMm(glbSize: Vector3): FreeDimensionMm {
  return {
    x: Math.round(glbSize.x * 1000),
    y: Math.round(glbSize.y * 1000),
    z: Math.round(glbSize.z * 1000),
  }
}

export const vector3ToFreeDimensionGlbBaseMm = vector3ToFreeDimensionMm

/**
 * Denominador fijo al colocar.
 * - Ancho: bbox si bbox &gt; catálogo.
 * - Alto: catálogo si bbox &gt; catálogo.
 * - Fondo: 2×catálogo − bbox si bbox &gt; catálogo.
 */
export function resolveFreeDimensionScaleBaseMm(
  catalog: FreeDimensionMm,
  bbox: FreeDimensionMm,
): FreeDimensionScaleBaseMm {
  const axis = (cat: number, box: number, name: 'Ancho' | 'Alto' | 'Fondo'): number => {
    if (box <= 0 && cat <= 0) return 1
    if (box <= 0) return cat
    if (cat <= 0) return box

    if (box > cat) {
      if (name === 'Fondo') {
        const panel = 2 * cat - box
        return panel > 0 ? panel : cat
      }
      if (name === 'Alto') {
        return cat
      }
      return box
    }
    return box
  }

  return {
    x: axis(catalog.x, bbox.x, 'Ancho'),
    y: axis(catalog.y, bbox.y, 'Alto'),
    z: axis(catalog.z, bbox.z, 'Fondo'),
  }
}

export function getFreeDimensionMorphBase(morph: Morphs | undefined, itemDim?: number): number {
  if (!morph) return itemDim && itemDim > 0 ? itemDim : 1
  if (morph._default != null && morph._default > 0) return morph._default
  if (morph.min === morph.max && morph.max > 0) return morph.max
  return morph.default ?? (itemDim && itemDim > 0 ? itemDim : 1)
}

function getGlbAxisMm(glbSize: Vector3, name: (typeof DIMENSION_MORPH_NAMES)[number]): number {
  const units = name === 'Ancho' ? glbSize.x : name === 'Alto' ? glbSize.y : glbSize.z
  return Math.round(units * 1000)
}

export function applyFreeDimensionGlbBaseMorphs(
  morphs: Morphs[],
  glbSize: Vector3,
  targets: { width: number; height: number; thickness: number },
): Morphs[] {
  const targetByName = {
    Ancho: targets.width,
    Alto: targets.height,
    Fondo: targets.thickness,
  } as const

  return morphs.map((m) => {
    if (!isFreeDimensionDimensionMorph(m.name)) return m
    const baseMm = getGlbAxisMm(glbSize, m.name as (typeof DIMENSION_MORPH_NAMES)[number])
    const targetMm = targetByName[m.name as keyof typeof targetByName] ?? m.default ?? baseMm
    return {
      ...m,
      _default: baseMm,
      min: baseMm,
      max: baseMm,
      default: targetMm,
    }
  })
}

export function getFreeDimensionMorphTarget(morph: Morphs | undefined, itemDim?: number): number {
  if (itemDim != null && itemDim > 0) return itemDim
  if (morph?.default != null && morph.default > 0) return morph.default
  return getFreeDimensionMorphBase(morph, itemDim)
}

/** Ancho por morph GLB; Alto por escala grupo; Fondo por morph GLB si bbox Z &gt; catálogo. */
export function getFreeDimensionRenderMorphs(
  morphs: Morphs[],
  _glbBaseMm?: FreeDimensionMm,
  _scaleBaseMm?: FreeDimensionScaleBaseMm,
  layoutMm?: Partial<FreeDimensionMm> & {
    width?: number
    height?: number
    thickness?: number
  },
  item?: {
    freeDimensionAnchoViaGlbMorph?: boolean
    freeDimensionAltoViaGlbMorph?: boolean
    freeDimensionFondoViaGlbMorph?: boolean
    thickness?: number
    catalogID?: string | number
    hasMorphs?: boolean
  },
): Morphs[] {
  const catalog = (item as { freeDimensionCatalogMm?: FreeDimensionCatalogMm })
    ?.freeDimensionCatalogMm

  return morphs.map((m) => {
    if (m.name === 'Ancho' && itemUsesFreeDimensionAnchoGlbMorph(item)) {
      if (m.min == null || m.max == null || m.max <= m.min) return m
      const w = layoutMm?.width
      const target = w != null && w > 0 ? w : (m._default ?? m.default ?? 0)
      const max = getFreeDimensionAnchoMorphMaxMm(m, catalog)
      return { ...m, default: Math.min(target, max), _default: target }
    }
    if (m.name === 'Alto') {
      if (itemUsesFreeDimensionAltoApiMorph(item)) {
        const sidebarAlto =
          layoutMm?.height != null && layoutMm.height > 0
            ? layoutMm.height
            : (m._default ?? m.default ?? 0)
        const target = clampMorphMm(sidebarAlto, m.min, m.max)
        return { ...m, _default: target, default: target, min: m.min, max: m.max }
      }
      const sidebarAlto =
        layoutMm?.height != null && layoutMm.height > 0
          ? layoutMm.height
          : (m._default ?? getFreeDimensionParentLayoutAltoMm(item) ?? m.default ?? 0)
      const glbRef = _glbBaseMm?.y ?? m.default ?? 0
      const morphDefault = usesFreeDimensionGlbZocaloSlabInAltoBbox(item)
        ? sidebarAlto
        : glbRef > 0
          ? glbRef
          : m.default
      return { ...m, _default: sidebarAlto, default: morphDefault, min: 0, max: 0 }
    }
    if (m.name === 'Fondo' && itemUsesFreeDimensionFondoGlbMorph(item)) {
      const t =
        layoutMm?.thickness != null && layoutMm.thickness > 0
          ? layoutMm.thickness
          : (m._default ?? item?.thickness ?? m.default ?? 0)
      const morphMax = getFreeDimensionFondoMorphMaxMm(m, catalog, _glbBaseMm)
      return applyFreeDimensionFondoGlbMorphMorph(m, t, morphMax)
    }
    if (
      m.name === 'Fondo' &&
      usesFreeDimensionScaling(item) &&
      isRbFreeDimensionRegleta(item)
    ) {
      const t =
        layoutMm?.thickness != null && layoutMm.thickness > 0
          ? layoutMm.thickness
          : (item?.thickness ?? m._default ?? 0)
      const glbRef = _glbBaseMm?.z ?? catalog?.z ?? m.default ?? 600
      return {
        ...m,
        _default: t,
        default: glbRef,
        min: 0,
        max: 0,
        show: true,
        modifyable: true,
      }
    }
    return m
  })
}

export function getEffectiveFreeDimensionBases(item: {
  width?: number
  height?: number
  thickness?: number
  freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  glbSize?: Vector3
  catalogID?: string | number
  hasMorphs?: boolean
}): {
  catalog: FreeDimensionCatalogMm
  bbox: FreeDimensionGlbBaseMm | null
  scaleBase: FreeDimensionScaleBaseMm | null
} {
  const bbox =
    item.freeDimensionGlbBaseMm ??
    (item.glbSize && item.glbSize.x > 0 && item.glbSize.y > 0 && item.glbSize.z > 0
      ? vector3ToFreeDimensionMm(item.glbSize)
      : null)
  const catalog: FreeDimensionCatalogMm = item.freeDimensionCatalogMm ?? {
    x: item.width ?? 0,
    y: usesFreeDimensionScaling(item)
      ? getFreeDimensionSidebarAlto(item.height, item)
      : (item.height ?? 0),
    z: item.thickness ?? 0,
  }
  const scaleBase =
    bbox && catalog.x > 0 && catalog.y > 0 && catalog.z > 0
      ? resolveFreeDimensionScaleBaseMm(catalog, bbox)
      : item.freeDimensionScaleBaseMm ?? null

  return { catalog, bbox, scaleBase }
}

/**
 * Escala de grupo estilo universal: width/glbX, height/glbY, fondo vía getFreeDimensionFondoGroupScaleZ.
 */
export function getFreeDimensionScaleRatios(item: {
  width?: number
  height?: number
  thickness?: number
  freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  glbSize?: Vector3
  catalogID?: string | number
  hasMorphs?: boolean
}): { x: number; y: number; z: number } {
  const { catalog, bbox } = getEffectiveFreeDimensionBases(item)

  const ratio = (target: number | undefined, base: number, fallback: number) => {
    if (base <= 0) return 1
    const t = target != null && target > 0 ? target : fallback
    return t / base
  }

  const glbX = item.freeDimensionGlbBaseMm?.x ?? bbox?.x ?? 0
  const scaleX =
    glbX > 0
      ? ratio(item.width, glbX, catalog.x)
      : ratio(item.width, item.freeDimensionScaleBaseMm?.x ?? 0, catalog.x)

  return {
    x: scaleX > 0 ? scaleX : 1,
    y: getFreeDimensionAltoGroupScaleY(item),
    z: getFreeDimensionFondoGroupScaleZ(item),
  }
}

export function getFreeDimensionSidebarMorphs(item: LinkedItem): Morphs[] {
  return DIMENSION_MORPH_NAMES.map((name, i) => {
    const existing = item.morphs?.find((m) => m.name === name)
    const defaultVal =
      name === 'Ancho'
        ? (item.width ?? existing?.default ?? 0)
        : name === 'Fondo'
          ? (item.thickness ?? existing?.default ?? 0)
          : usesFreeDimensionScaling(item)
            ? (getFreeDimensionSidebarAlto(item.height, item) ??
              item.freeDimensionCatalogMm?.y ??
              existing?.default ??
              0)
            : (item.height ?? existing?.default ?? 0)

    if (existing) {
      const offset = name === 'Alto' ? getFreeDimensionAltoOffsetMm(item) : 0
      const catalogAnchoMax = item.freeDimensionCatalogMm?.x
      const altoApiRange = name === 'Alto' && itemUsesFreeDimensionAltoApiMorph(item)
      const sidebarMin = altoApiRange
        ? (existing.min ?? 0)
        : name === 'Alto' && offset !== 0 && existing.min != null && existing.min > 0
          ? existing.min
          : name === 'Alto'
            ? 0
            : existing.min ?? 0
      let sidebarMax = altoApiRange
        ? (existing.max ?? 0)
        : name === 'Alto' && existing.max != null && existing.max > existing.min
          ? getFreeDimensionAltoSidebarMorphMaxMm(existing.max, item)
          : name === 'Alto'
            ? 0
            : existing.max ?? 0
      if (
        name === 'Ancho' &&
        isRbFreeDimensionRegleta(item) &&
        catalogAnchoMax != null &&
        catalogAnchoMax > 0
      ) {
        sidebarMax = catalogAnchoMax
      }
      return {
        ...existing,
        show: true,
        modifyable: true,
        default: defaultVal,
        min: sidebarMin,
        max: sidebarMax,
      }
    }

    return {
      morphId: -100 - i,
      order: i + 1,
      type: name === 'Ancho' ? 'A' : name === 'Fondo' ? 'F' : 'T',
      name,
      show: true,
      modifyable: true,
      default: defaultVal,
      min: 0,
      max: 0,
    } as Morphs
  })
}
